/**
 * Match Score Model
 * Handles detailed scoring with sets, games, and tiebreaks
 */
class MatchScore {
    /**
     * Create a new match score
     * @param {number} setsToWin - Number of sets needed to win (from config)
     * @param {number|null} gamesPerSet - Number of games per set (null if unlimited)
     * @param {number} tiebreakPoints - Points in tiebreak
     * @param {boolean} allowDraws - Whether draws are allowed
     */
    constructor(setsToWin = 1, gamesPerSet = null, tiebreakPoints = 7, allowDraws = true) {
        this.setsToWin = setsToWin;
        this.gamesPerSet = gamesPerSet;
        this.tiebreakPoints = tiebreakPoints;
        this.allowDraws = allowDraws;
        this.sets = [];
        this.winner = null; // 0, 1 (team index), or 'draw'
        this.stats = {
            team1: { setsWon: 0, setsLost: 0, gamesWon: 0, gamesLost: 0 },
            team2: { setsWon: 0, setsLost: 0, gamesWon: 0, gamesLost: 0 }
        };
    }

    /**
     * Add or update a set score
     * @param {number} setIndex - Set index (0, 1, 2)
     * @param {number} team1Games - Games won by team 1
     * @param {number} team2Games - Games won by team 2
     * @param {Object|null} tiebreak - Tiebreak score {team1: pts, team2: pts}
     */
    setSetScore(setIndex, team1Games, team2Games, tiebreak = null) {
        // Ensure sets array is large enough
        while (this.sets.length <= setIndex) {
            this.sets.push({ team1: 0, team2: 0, tiebreak: null });
        }

        // Update set score
        this.sets[setIndex] = {
            team1: team1Games,
            team2: team2Games,
            tiebreak: tiebreak
        };

        // Recalculate stats and winner
        this.calculate();
    }

    /**
     * Calculate match statistics and determine winner
     */
    calculate() {
        // Reset stats
        this.stats = {
            team1: { setsWon: 0, setsLost: 0, gamesWon: 0, gamesLost: 0 },
            team2: { setsWon: 0, setsLost: 0, gamesWon: 0, gamesLost: 0 }
        };

        // Calculate for each set
        this.sets.forEach(set => {
            // Count games
            this.stats.team1.gamesWon += set.team1;
            this.stats.team1.gamesLost += set.team2;
            this.stats.team2.gamesWon += set.team2;
            this.stats.team2.gamesLost += set.team1;

            // Determine set winner
            if (set.team1 > set.team2) {
                this.stats.team1.setsWon++;
                this.stats.team2.setsLost++;
            } else if (set.team2 > set.team1) {
                this.stats.team2.setsWon++;
                this.stats.team1.setsLost++;
            }
        });

        // Determine match winner
        if (this.stats.team1.setsWon >= this.setsToWin) {
            this.winner = 0;
        } else if (this.stats.team2.setsWon >= this.setsToWin) {
            this.winner = 1;
        } else if (this.allowDraws && this.isDrawCondition()) {
            this.winner = 'draw';
        } else {
            this.winner = null; // Match not finished
        }
    }

    /**
     * Check if match is in a draw condition
     * Draw occurs when games are equal in a set (e.g., 5-5, 6-6)
     * @returns {boolean}
     */
    isDrawCondition() {
        // Must have at least one set played
        if (this.sets.length === 0) {
            return false;
        }

        // For single set tournaments (setsToWin = 1)
        if (this.setsToWin === 1) {
            const set = this.sets[0];
            // Draw if games are equal
            return set && set.team1 === set.team2 && set.team1 > 0;
        }

        // For multi-set tournaments
        // Draw if sets are equal and at least one set has been played
        if (this.stats.team1.setsWon === this.stats.team2.setsWon) {
            const maxSets = this.setsToWin * 2 - 1;
            const setsPlayed = this.getSetsPlayed();
            return setsPlayed >= maxSets && this.stats.team1.setsWon > 0;
        }

        return false;
    }

    /**
     * Check if match is complete
     * @returns {boolean}
     */
    isComplete() {
        return this.winner !== null; // Can be 0, 1, or 'draw'
    }

    /**
     * Check if match ended in a draw
     * @returns {boolean}
     */
    isDraw() {
        return this.winner === 'draw';
    }

    /**
     * Get number of sets currently played
     * @returns {number}
     */
    getSetsPlayed() {
        return this.sets.filter(s => s.team1 > 0 || s.team2 > 0).length;
    }

    /**
     * Validate a set score
     * @param {number} team1Games - Games won by team 1
     * @param {number} team2Games - Games won by team 2
     * @returns {Object} {valid: boolean, error: string}
     */
    validateSetScore(team1Games, team2Games) {
        // Basic validation
        if (team1Games < 0 || team2Games < 0) {
            return { valid: false, error: 'Le score ne peut pas être négatif' };
        }

        // If draws allowed, accept ANY score (including equal scores like 5-5, 6-6)
        if (this.allowDraws) {
            return { valid: true };
        }

        // Empty score is always valid
        if (team1Games === 0 && team2Games === 0) {
            return { valid: true };
        }

        // All non-negative scores are valid (including equal scores like 5-5, 6-6)
        // The match result will be determined based on the overall sets won
        return { valid: true };
    }

    /**
     * Get current match status summary
     * @returns {string}
     */
    getStatusSummary() {
        const sets = `${this.stats.team1.setsWon}-${this.stats.team2.setsWon}`;
        const games = `${this.stats.team1.gamesWon}-${this.stats.team2.gamesWon}`;
        return `Sets: ${sets} | Jeux: ${games}`;
    }

    /**
     * Export to plain object (for storage)
     * @returns {Object}
     */
    toJSON() {
        return {
            sets: this.sets,
            winner: this.winner,
            stats: this.stats,
            config: {
                setsToWin: this.setsToWin,
                gamesPerSet: this.gamesPerSet,
                tiebreakPoints: this.tiebreakPoints,
                allowDraws: this.allowDraws
            }
        };
    }

    /**
     * Create from stored object
     * @param {Object} data - Stored data
     * @returns {MatchScore}
     */
    static fromJSON(data) {
        const score = new MatchScore(
            data.config.setsToWin,
            data.config.gamesPerSet,
            data.config.tiebreakPoints,
            data.config.allowDraws || false
        );
        score.sets = data.sets;
        score.winner = data.winner;
        score.stats = data.stats;
        return score;
    }
}

export default MatchScore;
